import { ConversationsV2AssistantMessagePhase } from '@spotify/confidence-graphql';

import { ToolExtensionOptions } from '../../extensions/tool-extension';
import { DisplayItem } from '../groupChatEvents';

export type ThoughtProcessItem = {
  type: 'thought-process';
  items: DisplayItem[];
  key: string;
};

export type DisplayItemWithThoughtProcess = DisplayItem | ThoughtProcessItem;

const isThinkingEvent = (item: DisplayItem): boolean =>
  item.type === 'event' && Boolean(item.event.thinking);

const isAssistantMessageEvent = (item: DisplayItem): boolean =>
  item.type === 'event' &&
  Boolean(item.event.assistantMessage) &&
  !item.event.error;

const isFinalAnswerEvent = (item: DisplayItem): boolean =>
  item.type === 'event' &&
  isAssistantMessageEvent(item) &&
  item.event.assistantMessage?.phase ===
    ConversationsV2AssistantMessagePhase.FinalAnswer;

const isUserMessageEvent = (item: DisplayItem): boolean =>
  item.type === 'event' && Boolean(item.event.userMessage);

const isProminentToolGroup = (
  item: DisplayItem,
  toolExtensions: ToolExtensionOptions[],
): boolean => {
  if (item.type !== 'tool-group') return false;
  if (item.entries.length !== 1) return false;
  const ext = toolExtensions.find(e => e.predicate(item.entries[0].tool));
  return ext?.display === 'prominent';
};

const isConfirmationResponseEvent = (item: DisplayItem): boolean =>
  item.type === 'event' && Boolean(item.event.toolConfirmationResponse);

const isThoughtMaterial = (
  item: DisplayItem,
  toolExtensions: ToolExtensionOptions[],
): boolean => {
  if (isFinalAnswerEvent(item)) return false;
  return (
    isThinkingEvent(item) ||
    (item.type === 'tool-group' &&
      !isProminentToolGroup(item, toolExtensions)) ||
    isAssistantMessageEvent(item)
  );
};

const findFinalAnswerIndex = (buffer: DisplayItem[]): number => {
  for (let i = buffer.length - 1; i >= 0; i--) {
    if (isFinalAnswerEvent(buffer[i])) return i;
  }
  for (let i = buffer.length - 1; i >= 0; i--) {
    const item = buffer[i];
    if (
      item.type === 'event' &&
      isAssistantMessageEvent(item) &&
      !item.event.assistantMessage?.phase
    ) {
      return i;
    }
  }
  return -1;
};

const isNonProminentToolGroup = (
  item: DisplayItem,
  toolExtensions: ToolExtensionOptions[],
): boolean =>
  item.type === 'tool-group' && !isProminentToolGroup(item, toolExtensions);

export const groupThoughtProcess = (
  displayItems: DisplayItem[],
  isRunning: boolean,
  toolExtensions: ToolExtensionOptions[],
): DisplayItemWithThoughtProcess[] => {
  const result: DisplayItemWithThoughtProcess[] = [];
  let buffer: DisplayItem[] = [];
  let bufferKey: string | undefined;

  const hasThinkingOrTools = (items: DisplayItem[]): boolean =>
    items.some(
      item =>
        isThinkingEvent(item) || isNonProminentToolGroup(item, toolExtensions),
    );

  const mergeOrPushThoughtProcess = (items: DisplayItem[], key: string) => {
    const last = result[result.length - 1];
    if (last?.type === 'thought-process') {
      result[result.length - 1] = {
        ...last,
        items: [...last.items, ...items],
      };
    } else {
      result.push({ type: 'thought-process', items, key });
    }
  };

  const flush = (isFinalSegment: boolean) => {
    if (buffer.length === 0) return;

    if (!hasThinkingOrTools(buffer)) {
      result.push(...buffer);
      buffer = [];
      bufferKey = undefined;
      return;
    }

    const isActive = isFinalSegment && isRunning;
    const answerIdx = isActive ? -1 : findFinalAnswerIndex(buffer);

    if (answerIdx === -1) {
      mergeOrPushThoughtProcess(
        [...buffer],
        `tp-${bufferKey ?? buffer[0].key}`,
      );
    } else {
      const thoughtItems = [
        ...buffer.slice(0, answerIdx),
        ...buffer.slice(answerIdx + 1),
      ];
      if (thoughtItems.length > 0) {
        mergeOrPushThoughtProcess(
          thoughtItems,
          `tp-${bufferKey ?? thoughtItems[0].key}`,
        );
      }
      result.push(buffer[answerIdx]);
    }

    buffer = [];
    bufferKey = undefined;
  };

  let deferredResponses: DisplayItem[] = [];

  for (const item of displayItems) {
    if (isUserMessageEvent(item)) {
      flush(false);
      result.push(...deferredResponses);
      deferredResponses = [];
      result.push(item);
      continue;
    }

    if (isConfirmationResponseEvent(item)) {
      deferredResponses.push(item);
      continue;
    }

    if (isThoughtMaterial(item, toolExtensions)) {
      if (isThinkingEvent(item) && buffer.some(isAssistantMessageEvent)) {
        flush(false);
      }
      if (!bufferKey) bufferKey = item.key;
      buffer.push(item);
      continue;
    }

    flush(false);
    result.push(...deferredResponses);
    deferredResponses = [];
    result.push(item);
  }

  flush(true);
  if (!isRunning) {
    result.push(...deferredResponses);
  }

  return result;
};

export const findLastThoughtProcessIdx = (
  items: readonly DisplayItemWithThoughtProcess[],
): number => items.findLastIndex(item => item.type === 'thought-process');
